import { FC, RefObject } from 'react'
import { AnimatePresence } from 'framer-motion'
import HorizontalSeparatorIcon from '@/assets/images/horizontal-separator.svg?react'
import {
    SectionWrapper,
    SectionsContainer,
    PopupContainer,
    Footer,
    HorizontalSeparator,
    popupVariants,
    sectionVariants,
    footerVariants,
} from './FilterPopup.styles'
import { FilterPopupProps } from './FilterPopup.types'
import { useFilterPopup } from './useFilterPopup'
import { Section } from './components/Section'
import { AnimatedDiv } from '../animatedDiv/animatedDiv.styles'
import { ActionButton } from '../buttons/action'
import { TextButton } from '../buttons/textButton'

interface FilterPopupWithRefProps extends FilterPopupProps {
    activatorRef?: RefObject<HTMLElement>
    isOpen: boolean
    onToggle: () => void
}

const FilterPopup: FC<FilterPopupWithRefProps> = ({
    children,
    sections,
    onApply,
    onCancel,
    applyLabel = 'Apply filters',
    cancelLabel = 'Cancel',
    className,
    defaultOpen = false,
    activatorRef,
    isOpen,
    onToggle,
}) => {
    const {
        popupRef,
        collapsedSections,
        searchValues,
        toggleCollapse,
        updateSearch,
        toggleOption,
        getFilteredOptions,
        getSelectedForSection,
        handleApply,
    } = useFilterPopup({
        sections,
        defaultOpen,
        onApply,
        onCancel,
        activatorRef,
        isOpen,
        onToggle,
    })

    return (
        <>
            {children}
            <AnimatePresence>
                {isOpen && (
                    <PopupContainer
                        ref={popupRef}
                        variants={popupVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        data-testid="filter-menu"
                    >
                        <SectionWrapper className={className}>
                            <SectionsContainer>
                                {sections.map((section, index) => {
                                    const isCollapsed =
                                        section.collapsible &&
                                        collapsedSections[section.id]
                                    const options = getFilteredOptions(section)
                                    const sectionSelectedOptions =
                                        getSelectedForSection(section.id)

                                    return (
                                        <div key={section.id}>
                                            <AnimatedDiv
                                                variants={sectionVariants}
                                                custom={index}
                                            >
                                                <Section
                                                    section={section}
                                                    isCollapsed={!!isCollapsed}
                                                    searchValue={
                                                        searchValues[
                                                            section.id
                                                        ] ?? ''
                                                    }
                                                    onCollapseToggle={
                                                        toggleCollapse
                                                    }
                                                    onSearchChange={
                                                        updateSearch
                                                    }
                                                    options={options}
                                                    selectedOptionIds={
                                                        sectionSelectedOptions
                                                    }
                                                    onOptionToggle={(
                                                        optionId: string
                                                    ) => {
                                                        toggleOption(
                                                            section.id,
                                                            optionId
                                                        )
                                                    }}
                                                />
                                            </AnimatedDiv>
                                            {index < sections.length - 1 && (
                                                <HorizontalSeparator>
                                                    <HorizontalSeparatorIcon />
                                                </HorizontalSeparator>
                                            )}
                                        </div>
                                    )
                                })}
                            </SectionsContainer>
                            <AnimatedDiv variants={footerVariants}>
                                <Footer>
                                    <TextButton
                                        text={cancelLabel}
                                        onClick={handleApply}
                                        dataTestId="cancel-filter-button"
                                        fontSizeVariant="m-semibold"
                                    />
                                    <ActionButton
                                        dataTestId="filter-apply-button"
                                        text={applyLabel}
                                        onClick={handleApply}
                                    />
                                </Footer>
                            </AnimatedDiv>
                        </SectionWrapper>
                    </PopupContainer>
                )}
            </AnimatePresence>
        </>
    )
}

export default FilterPopup
